/**
 * Support Request Detail Page - View support request details (Patient)
 *
 * Mobile-friendly full page for viewing request details and responses.
 */

import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Router, type RouterLocation } from "@vaadin/router";
import { supportService } from "../services/support";
import type { SupportRequestDetail, SupportResponse } from "../types/support";
import { PRIORITY_INFO, STATUS_INFO } from "../types/support";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/spinner/spinner.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import "@awesome.me/webawesome/dist/components/badge/badge.js";
import "../components/app-shell";

@customElement("support-request-detail-page")
export class SupportRequestDetailPage extends LitElement {
  @property({ type: Object }) location?: RouterLocation;

  @state() private requestDetail: SupportRequestDetail | null = null;
  @state() private isLoading = true;
  @state() private error = "";

  static styles = css`
    :host {
      display: block;
    }

    .page-content {
      padding: 1rem;
      max-width: 600px;
      margin: 0 auto;
    }

    .back-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 1.5rem;
      padding-bottom: 1rem;
      border-bottom: 1px solid #e9ecef;
    }

    .back-header h1 {
      margin: 0;
      font-size: 1.25rem;
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .request-header {
      margin-bottom: 1.5rem;
    }

    .request-subject {
      font-size: 1.125rem;
      font-weight: 600;
      color: #333;
      margin: 0 0 0.75rem 0;
    }

    .request-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      font-size: 0.875rem;
    }

    .meta-item {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      color: #666;
    }

    .priority-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      font-size: 0.75rem;
      font-weight: 500;
    }

    .section {
      margin-bottom: 1.5rem;
    }

    .section-label {
      font-size: 0.75rem;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 0.5rem;
    }

    .initial-message {
      background: #f8f9fa;
      border-radius: 0.5rem;
      padding: 1rem;
      white-space: pre-wrap;
      line-height: 1.5;
    }

    .responses-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .response-item {
      background: #e3f2fd;
      border-radius: 0.5rem;
      padding: 0.75rem;
    }

    .response-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
      font-size: 0.8125rem;
    }

    .response-author {
      font-weight: 500;
      color: #1565c0;
    }

    .response-date {
      color: #666;
    }

    .response-message {
      white-space: pre-wrap;
      line-height: 1.5;
    }

    .resolution-box {
      background: #e8f5e9;
      border: 1px solid #4caf50;
      border-radius: 0.5rem;
      padding: 1rem;
    }

    .resolution-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-weight: 600;
      color: #2e7d32;
      margin-bottom: 0.5rem;
    }

    .resolution-meta {
      font-size: 0.8125rem;
      color: #666;
      margin-bottom: 0.5rem;
    }

    .resolution-note {
      white-space: pre-wrap;
      line-height: 1.5;
    }

    .empty-responses {
      text-align: center;
      padding: 2rem;
      color: #666;
      background: #f8f9fa;
      border-radius: 0.5rem;
    }

    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 3rem;
      text-align: center;
      color: #666;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._loadRequest();
  }

  updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has("location")) {
      this._loadRequest();
    }
  }

  private get requestId(): string {
    return (this.location?.params?.id as string) || "";
  }

  private async _loadRequest() {
    if (!this.requestId) {
      this.error = "No request ID provided";
      this.isLoading = false;
      return;
    }

    try {
      this.isLoading = true;
      this.error = "";
      this.requestDetail = await supportService.getRequestDetail(
        this.requestId,
      );
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to load request";
    } finally {
      this.isLoading = false;
    }
  }

  private _goBack() {
    Router.go("/support");
  }

  private _formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-NZ", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  render() {
    return html`
      <app-shell pageTitle="Request Details">
        <div class="page-content">
          <div class="back-header">
            <wa-button variant="text" size="small" @click=${this._goBack}>
              <wa-icon name="arrow-left"></wa-icon>
            </wa-button>
            <h1>Support Request</h1>
          </div>

          ${this.error
            ? html`
                <wa-callout variant="danger" open>
                  <wa-icon slot="icon" name="octagon-exclamation"></wa-icon>
                  ${this.error}
                </wa-callout>
              `
            : null}
          ${this.isLoading
            ? html`
                <div class="loading-container">
                  <wa-spinner style="font-size: 2rem;"></wa-spinner>
                  <p>Loading...</p>
                </div>
              `
            : this.requestDetail
              ? this._renderRequest()
              : null}
        </div>
      </app-shell>
    `;
  }

  private _renderRequest() {
    const { request, responses } = this.requestDetail!;
    const priorityInfo = PRIORITY_INFO[request.priority];
    const statusInfo = STATUS_INFO[request.status];

    return html`
      <div class="request-header">
        <h2 class="request-subject">${request.subject}</h2>
        <div class="request-meta">
          <span
            class="priority-badge"
            style="background: ${priorityInfo.color}22; color: ${priorityInfo.color}"
          >
            ${priorityInfo.label}
          </span>
          <wa-badge
            variant="${request.status === "resolved" ? "success" : "neutral"}"
          >
            ${statusInfo.label}
          </wa-badge>
          <span class="meta-item">
            <wa-icon name="calendar-days"></wa-icon>
            ${this._formatDate(request.createdAt)}
          </span>
        </div>
      </div>

      <div class="section">
        <div class="section-label">Your Message</div>
        <div class="initial-message">${request.initialNote}</div>
      </div>

      ${request.status === "resolved" && request.resolutionNote
        ? html`
            <div class="section">
              <div class="section-label">Resolution</div>
              <div class="resolution-box">
                <div class="resolution-header">
                  <wa-icon name="circle-check"></wa-icon>
                  Resolved
                </div>
                <div class="resolution-meta">
                  By ${request.resolvedByName} on
                  ${this._formatDate(request.resolvedAt!)}
                </div>
                <div class="resolution-note">${request.resolutionNote}</div>
              </div>
            </div>
          `
        : null}
      ${responses.length > 0
        ? html`
            <div class="section">
              <div class="section-label">Responses (${responses.length})</div>
              <div class="responses-list">
                ${responses.map((r) => this._renderResponse(r))}
              </div>
            </div>
          `
        : html`
            <div class="section">
              <div class="section-label">Responses</div>
              <div class="empty-responses">
                <wa-icon
                  name="comment-dots"
                  style="font-size: 2rem; margin-bottom: 0.5rem; display: block;"
                ></wa-icon>
                No responses yet. Your clinic will respond soon.
              </div>
            </div>
          `}
    `;
  }

  private _renderResponse(response: SupportResponse) {
    return html`
      <div class="response-item">
        <div class="response-header">
          <span class="response-author">${response.clinicianName}</span>
          <span class="response-date"
            >${this._formatDate(response.createdAt)}</span
          >
        </div>
        <div class="response-message">${response.message}</div>
      </div>
    `;
  }
}
